import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { LuBellOff, LuCheckCheck, LuX } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useNotificationDrawer } from './NotificationDrawerContext.jsx';
import { useUnreadNotifications } from '../../hooks/useUnreadNotifications';
import NotificationList from './NotificationList.jsx';
import './NotificationDrawer.less';

/**
 * NotificationDrawer — global, breakpoint-aware notification surface.
 *
 * Mounted once inside AppShell. Opens from the bell in MobileTopBar
 * (mobile) or DesktopSidebar (desktop / tablet). The current page
 * stays mounted underneath, so scroll position + form state are
 * preserved when the user closes the drawer.
 *
 * Layouts:
 *   - mobile (bp === 'mobile') → bottom sheet, rounded top corners,
 *     drag handle, up to ~90dvh, safe-area-aware bottom padding.
 *   - desktop / tablet (anything else) → right-side slide-out drawer,
 *     ~400px wide, full viewport height, restrained shadow.
 *
 * Backdrop: dim + subtle blur; click closes (matches the shared
 * ResponsiveDialog contract so the two surfaces feel consistent).
 *
 * A11y: role="dialog" aria-modal="true"; Escape closes; focus is
 * moved into the panel on open and returned to the opener on close
 * via NotificationDrawerContext. Body scroll is locked while open so
 * the underlying page can't skate under the panel on iOS.
 *
 * Content: no page header, no giant title — a single 15/600 title
 * with an inline unread count on the left, "Mark all as read" +
 * close-icon on the right. The list itself is the body.
 */
function NotificationDrawer() {
    const { isOpen, close } = useNotificationDrawer();
    const bp = useBreakpoint();
    const navigate = useNavigate();
    const { unread, hasUnread, notifications, isLoading, error, markAllAsRead, markAsRead, refresh } = useUnreadNotifications();
    const panelRef = useRef(null);
    const closeBtnRef = useRef(null);

    const layout = bp === 'mobile' ? 'sheet' : 'drawer';

    // Escape handler + body-scroll lock. Kept in a single effect so
    // teardown always happens even if `isOpen` flips false while
    // another effect is pending.
    useEffect(() => {
        if (!isOpen) return undefined;

        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Move focus into the panel — prefer the close button so
        // Escape-then-Enter / Escape-then-Space are both immediate
        // dismissal paths for keyboard users.
        const raf = window.requestAnimationFrame
            ? window.requestAnimationFrame
            : (cb) => setTimeout(cb, 0);
        const handle = raf(() => {
            (closeBtnRef.current || panelRef.current)?.focus?.();
        });

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(handle);
            }
        };
    }, [isOpen, close]);

    const onNotificationClick = useCallback((item) => {
        if (!item) return;
        // Mark as read first so the unread count updates before nav.
        if (typeof markAsRead === 'function' && item.id != null) {
            markAsRead(item.id);
        }
        // No target → keep the panel open so the user can pick
        // something else, matching the "don't nav to a broken route"
        // rule.
        if (!item.href) return;
        close();
        navigate(item.href);
    }, [close, markAsRead, navigate]);

    const onMarkAllRead = useCallback(() => {
        if (typeof markAllAsRead === 'function') markAllAsRead();
    }, [markAllAsRead]);

    const titleId = 'gc-notification-drawer-title';
    const rootClass = useMemo(() => (
        `gc-notification-drawer gc-notification-drawer--${layout}${isOpen ? ' is-open' : ''}`
    ), [layout, isOpen]);

    // Render nothing until the drawer opens the first time — keeps
    // the DOM tree clean and avoids any accidental interaction with
    // aria trees while closed.
    if (!isOpen) return null;

    return (
        <div className={rootClass} role="presentation">
            <div
                className="gc-notification-drawer__backdrop"
                onClick={close}
                aria-hidden="true"
            />
            <aside
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="gc-notification-drawer__panel"
                tabIndex={-1}
            >
                <header className="gc-notification-drawer__header">
                    <h2 id={titleId} className="gc-notification-drawer__title">
                        Notifications
                        {hasUnread && (
                            <span
                                className="gc-notification-drawer__badge"
                                aria-label={`${unread} unread`}
                            >
                                {unread > 99 ? '99+' : unread}
                            </span>
                        )}
                    </h2>
                    <div className="gc-notification-drawer__actions">
                        {hasUnread && (
                            <button
                                type="button"
                                className="gc-notification-drawer__mark-all"
                                onClick={onMarkAllRead}
                            >
                                <LuCheckCheck aria-hidden="true" />
                                <span>Mark all as read</span>
                            </button>
                        )}
                        <button
                            ref={closeBtnRef}
                            type="button"
                            className="gc-notification-drawer__close"
                            onClick={close}
                            aria-label="Close notifications"
                        >
                            <LuX aria-hidden="true" />
                        </button>
                    </div>
                </header>

                <div className="gc-notification-drawer__body">
                    <NotificationList
                        notifications={notifications}
                        isLoading={isLoading}
                        error={error}
                        onSelect={onNotificationClick}
                        onRetry={refresh}
                        emptyIcon={<LuBellOff aria-hidden="true" />}
                    />
                </div>
            </aside>
        </div>
    );
}

export default NotificationDrawer;
